import {useAgencyQuery} from "@/api/agencyApi";
import {useCurrentCompanyId} from "@/api/companyApi";
import {useAgencyStore} from "@/store/agency";
import {useCompanyStore} from "@/store/company";
import {selectedCompanyIdAtom} from "@/store/jotaiAtoms";
import useTokenStore from "@/store/token";
import useUserStore from "@/store/user";
import {getUserPermission} from "@/utils/permissionUtils";
import {useLocation, useParams} from "@tanstack/react-router";
import {getDefaultStore, useAtomValue} from "jotai";
import {useEffect} from "react";

export const useIsLoggedIn = () => useTokenStore(state => !!state.userId);

export const useIsAdmin = (role?: "COMPANY_ADMIN" | "INFLUENCER_ADMIN") => {
  const accessLevel = useUserStore.getState().user.user_access_level;

  if (role) {
    return accessLevel === role;
  }

  return accessLevel === "COMPANY_ADMIN" || accessLevel === "INFLUENCER_ADMIN";
};
export const useIsCompany = ({excludeAdmins = false}: {excludeAdmins?: boolean} = {}) =>
  useUserStore(
    state =>
      state.user.user_access_level === "SINGLE_BRAND" ||
      (!excludeAdmins ? state.user.user_access_level === "COMPANY_ADMIN" : false),
  );
export const useIsAgency = () =>
  useUserStore.getState().user.user_access_level === "AGENCY" ||
  useUserStore.getState().user.agencies?.[0]?.agency.multiple_brands;
export const useIsSubbrand = () => useUserStore.getState().user.user_access_level === "SUB_BRAND";
export const useGenerativeAiAllowed = () => {
  const generativeAiDisabled = useUserStore(
    state =>
      (state.user.agencies?.some(membership => membership.agency.disable_generative_ai) ?? false) ||
      (state.user.sub_brands?.some(membership => membership.company.disable_generative_ai) ?? false),
  );
  return !generativeAiDisabled;
};
export const useIntercomDisabled = () =>
  useUserStore(
    state =>
      (state.user.agencies?.some(membership => membership.agency.disable_intercom) ?? false) ||
      (state.user.sub_brands?.some(membership => membership.company.disable_intercom) ?? false),
  );
export const useUserId = () => useUserStore(state => Number(state.user.id));
export const useAgencyId = () => {
  const {agencyId} = useParams({strict: false});
  const isAdmin = useIsAdmin();
  const isAgency = useIsAgency();
  const userAgencyId = useUserStore(state => state.user.agencies?.at(0)?.agency?.id);

  if (isAdmin) {
    return agencyId ? Number(agencyId) : undefined;
  }

  if (isAgency) {
    return userAgencyId;
  }

  return undefined;
};
export const useAgencySeats = () => {
  const isAdmin = useIsAdmin();

  if (isAdmin) {
    return (
      useAgencyStore.getState().agency?.package_subscription?.number_of_seats_left ??
      useAgencyStore.getState().agency?.future_subscription?.number_of_seats_left ??
      useCompanyStore.getState().company?.package_subscription?.number_of_seats_left ??
      useCompanyStore.getState().company?.future_subscription?.number_of_seats_left ??
      100_000
    );
  }

  return useUserStore.getState().user.package_subscription?.number_of_seats_left ?? 100_000;
};
export const useCampaignsLeft = () => {
  const isAdmin = useIsAdmin();

  if (isAdmin) {
    return (
      useAgencyStore.getState().agency?.package_subscription?.number_of_campaigns_left ??
      useAgencyStore.getState().agency?.future_subscription?.number_of_campaigns_left ??
      useCompanyStore.getState().company?.package_subscription?.number_of_campaigns_left ??
      useCompanyStore.getState().company?.future_subscription?.number_of_campaigns_left ??
      100_000
    );
  }

  return useUserStore.getState().user.package_subscription?.number_of_campaigns_left ?? 100_000;
};

export const useIsInfuencer = ({excludeAdmins = false}: {excludeAdmins?: boolean} = {}) =>
  useUserStore(
    state =>
      state.user.user_access_level === "INFLUENCER" ||
      (!excludeAdmins ? state.user.user_access_level === "INFLUENCER_ADMIN" : false),
  );

export const useIsOwnProfile = () => {
  const location = useLocation();
  const {agencyId: agencyIdFromUrl} = useParams({strict: false});
  const companyId = useCurrentCompanyId();
  const loggedInUser = useUserStore(state => state.user);

  const numericCompanyId = companyId ? Number(companyId) : null;
  const numericAgencyId = agencyIdFromUrl ? Number(agencyIdFromUrl) : null;

  const isViewingOwnDashboard =
    (/^\/company\/\d+\/dashboard\/?$/.test(location.pathname) &&
      !!loggedInUser.sub_brands?.find(subBrand => subBrand?.company?.id === numericCompanyId)) ||
    (/^\/agency\/\d+\/dashboard\/?$/.test(location.pathname) &&
      !!loggedInUser.agencies?.find(agency => agency?.agency?.id === numericAgencyId));

  return (
    !!loggedInUser.sub_brands?.find(subBrand => subBrand?.company?.id === numericCompanyId) || isViewingOwnDashboard
  );
};

export const useHasPlan = () =>
  useUserStore(state => state.user.user_type === "admin" || !!state.user.package_subscription);

export const useChartAccess = () =>
  useUserStore(
    state =>
      (!!state.user.package_subscription && state.user.package_subscription.name !== "STARTER") ||
      state.user.user_type === "admin",
  );

export const useSelectedCompanyId = () => {
  return getDefaultStore().get(selectedCompanyIdAtom);
};

export const useSelectedCompany = () => {
  const selectedCompanyId = getDefaultStore().get(selectedCompanyIdAtom);
  return useUserStore.getState().user.sub_brands?.find(subBrand => subBrand.company.id === selectedCompanyId)?.company;
};

export const useSelectedCompanyReactive = () => {
  const selectedCompanyId = useAtomValue(selectedCompanyIdAtom);
  const company = useUserStore(
    state => state.user.sub_brands?.find(subBrand => subBrand.company.id === selectedCompanyId)?.company,
  );

  return company;
};

export const useSelectedAgency = () => {
  const {agencyId} = useParams({strict: false});
  const isAdmin = useIsAdmin();
  const userAgency = useUserStore(state => state.user.agencies?.at(0)?.agency);
  const storedAgency = useAgencyStore(state => state.agency);
  const {data: agency} = useAgencyQuery({
    agencyId: Number(agencyId),
    queryOptions: {
      enabled: !!agencyId && isAdmin,
    },
  });

  useEffect(() => {
    if (agency) {
      useAgencyStore.setState({agency});
    }
  }, [agency]);

  return isAdmin ? storedAgency : userAgency;
};

export const useUserPermission = ({
  companyId,
  excludeAdmins = false,
}: {companyId?: number; excludeAdmins?: boolean} = {}) => {
  const user = useUserStore.getState().user;
  const selectedCompanyId = companyId ?? getDefaultStore().get(selectedCompanyIdAtom);
  const selectedSubBrand = user.sub_brands?.find(subBrand => subBrand.company.id === selectedCompanyId);
  const userType =
    excludeAdmins && user.user_type === "admin" ? (user.is_company ? "company" : "influencer") : user.user_type!;

  return getUserPermission({
    ...selectedSubBrand,
    user_type: userType,
    is_company: user.is_company,
  });
};
